<template lang="pug">
.checklist-passing-launcher(v-if="currentChecklist")
  q-btn.checklist-passing-launcher__button(no-caps, unelevated, @click="openChecklist")
    inline-svg.checklist-passing-launcher__button-icon(:src="require('@/assets/icons/checklist/btn_icon.svg')")
    span.checklist-passing-launcher__button-label {{ buttonLabel }}

  checklist-passing-show(
    v-if="showOpened",
    @close-show="closeShow",
    @checklist-updated="onChecklistUpdated",
    :parentData="modalParentData"
  )

  checklist-passing-edit(v-if="editOpened", @close-form="closeEdit", :parentData="modalParentData")
</template>

<script setup>
import { checkliststLocales } from "@/services/useLocales";
import { computed, ref, watch } from "vue";

import checklistPassingEdit from "./edit.vue";
import checklistPassingShow from "./show.vue";

const completedChecklistStatuses = ["completed", "completed_with_deviation"];

const props = defineProps({
  /** Найденный issue-checklist, по которому выбирается режим открытия. */
  checklist: {
    type: Object,
    default: () => null,
  },
  /** Данные строки заявки и параметры открытия модального окна. */
  parentData: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits([
  /** Эмит обновления чеклиста после сохранения или завершения прохождения. */
  "checklist-updated",
]);

const showOpened = ref(false);
const editOpened = ref(false);
const currentChecklist = ref(props.checklist);

watch(
  () => props.checklist,
  newChecklist => {
    currentChecklist.value = newChecklist;
  },
  { deep: true },
);

const isEditable = computed(() => Boolean(currentChecklist.value?.can_edit));
const isCompleted = computed(() => completedChecklistStatuses.includes(currentChecklist.value?.status));
const isStarted = computed(() => Boolean(currentChecklist.value?.checklist_started));
const buttonLabel = computed(() => {
  if (isCompleted.value || !isEditable.value) {
    return checkliststLocales.value.show_checklist;
  }

  if (isStarted.value) {
    return checkliststLocales.value.continue_checklist ?? "Продолжить чеклист";
  }

  return checkliststLocales.value.start_checklist;
});

const requestPath = computed(() => {
  const rowId = props.parentData?.row?.id;
  const grid = props.parentData?.grid;

  if (!rowId) {
    return "";
  }

  if (["dynamic_issues", "issues", "archive"].includes(grid)) {
    return `/api/v3/dynamic/issues/${rowId}/checklist`;
  }

  if (["ppr_issues", "ppr_archive"].includes(grid)) {
    return `/api/v3/ppr/issues/${rowId}/checklist`;
  }

  return props.parentData?.requestPath ?? props.parentData?.request_path ?? "";
});

const modalParentData = computed(() => ({
  ...props.parentData,
  requestPath: requestPath.value,
}));

const openChecklist = () => {
  if (isEditable.value && !isCompleted.value) {
    editOpened.value = true;
    return;
  }

  showOpened.value = true;
};

const closeShow = () => {
  showOpened.value = false;
};

const onChecklistUpdated = data => {
  if (data?.data) {
    currentChecklist.value = data.complete && !data.data.status ? { ...data.data, status: "completed" } : data.data;
  }

  emit("checklist-updated", data);
};

const closeEdit = data => {
  if (data?.data) {
    currentChecklist.value = data.complete && !data.data.status ? { ...data.data, status: "completed" } : data.data;
    emit("checklist-updated", data);
  }

  editOpened.value = false;
};
</script>

<style scoped lang="scss">
.checklist-passing-launcher {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  margin-left: auto;

  &__button {
    min-height: 40px;
    padding: 0 18px;
    border-radius: 25px;
    background: #5b7aff !important;
    color: white !important;

    :deep(.q-btn__content) {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      font-weight: 600;
      line-height: 21px;
    }
  }

  &__button-icon {
    width: 14px;
    height: 14px;
    flex: 0 0 auto;
  }

  &__button-label {
    color: inherit;
  }
}
</style>
